import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { formatLocalizedDate, parseDateValue } from '@/lib/date-utils';
import { differenceInCalendarDays } from 'date-fns';
import { ArrowRight, CalendarDays, Sparkles } from 'lucide-react';

import {
  DealStatusBadge,
  getDealListStatus,
  type DealListRow,
} from './deal-status-badge';

export function DealRow({ deal }: { deal: DealListRow }) {
  const { t, i18n } = useTranslation();
  const status = getDealListStatus(deal);
  const date = parseDateValue(deal.startDate);
  const days = date ? differenceInCalendarDays(date, new Date()) : null;
  const managerName =
    deal.manager.name === 'Unknown'
      ? t('deals-list.unknown')
      : deal.manager.name;
  const url = `/deal/${encodeURIComponent(deal.id)}`;
  const destination =
    status === 'notStarted'
      ? `${url}/start`
      : status === 'csv'
        ? `${url}/csv/corrections`
        : url;
  const action =
    status === 'notStarted'
      ? 'enter'
      : status === 'csv'
        ? 'corrections'
        : status === 'closed'
          ? 'view'
          : 'resume';
  return (
    <TableRow>
      <TableCell className="w-[58%] px-5 py-4">
        <div className="font-semibold">{deal.dealName}</div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <CalendarDays size={14} />
          <span>{date ? formatLocalizedDate(date, i18n.language) : '—'}</span>
          {days !== null && (
            <span>
              ·{' '}
              {t(days >= 0 ? 'deals-list.inDays' : 'deals-list.daysAgo', {
                count: Math.abs(days),
              })}
            </span>
          )}
          <Avatar className="ml-1 h-5 w-5 text-[9px]">
            <AvatarImage src={deal.manager.photoUrl ?? undefined} alt="" />
            <AvatarFallback>
              {managerName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span>{managerName}</span>
        </div>
      </TableCell>
      <TableCell className="px-5">
        <DealStatusBadge deal={deal} />
      </TableCell>
      <TableCell className="px-5">
        <div className="flex justify-end gap-2">
          {status === 'notStarted' && (
            <Button variant="outline" asChild>
              <Link to={url} state={{ openImport: true }}>
                <Sparkles />
                {t('deals-list.actions.generate')}
              </Link>
            </Button>
          )}
          <Button asChild variant={status === 'closed' ? 'outline' : 'default'}>
            <Link to={destination}>
              {t(`deals-list.actions.${action}`)}
              {status !== 'closed' && <ArrowRight />}
            </Link>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
